import React from 'react';
import { Box, Typography, Fade, Paper, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { QRCodeSVG } from 'qrcode.react';
import { AppStoreButton, GooglePlayButton } from 'react-mobile-app-button';
import type { GlobalDialogProps } from '../../../../types';

// Reuse the same animation duration as in the parent component
const ANIMATION_DURATION = 400;

// Use the same URLs as in the MobileAppDialog
const MOBILE_URL = 'https://screenapp.io/mobile?utm_source=onboarding';

interface RecordFromPhoneScreenProps {
  onClose?: GlobalDialogProps['onClose'];
}

const RecordFromPhoneScreen: React.FC<RecordFromPhoneScreenProps> = () => {
  const theme = useTheme();

  return (
    <Fade in timeout={ANIMATION_DURATION}>
      <Box
        sx={{
          width: '100%',
          textAlign: 'center',
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 300, mb: 2 }}>
          Download Our Mobile App
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ mb: 4, textAlign: 'center', maxWidth: '700px', mx: 'auto' }}
        >
          Use our mobile app to record videos, audio, or screen recordings
          directly from your phone
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: '500px',
            width: '100%',
            bgcolor:
              theme.palette.mode === 'dark'
                ? 'background.paper'
                : theme.palette.grey[50],
            borderRadius: 4,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center',
                alignItems: { xs: 'center', sm: 'flex-start' },
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <AppStoreButton url={MOBILE_URL} theme="light" height={46} />
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  opacity: 0.7,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      borderRadius: 1,
                      cursor: 'not-allowed',
                    },
                    pointerEvents: 'none',
                  }}
                >
                  <GooglePlayButton url="#" theme="light" height={46} />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 0.5,
                    fontWeight: 'bold',
                  }}
                >
                  Coming Soon
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body2"
              sx={{
                width: '100%',
                textAlign: 'center',
                color: 'text.secondary',
                my: 1,
              }}
            >
              Or scan this QR code with your phone
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                width: 'fit-content',
              }}
            >
              <QRCodeSVG
                value={MOBILE_URL}
                size={140}
                level="M"
                includeMargin={false}
                fgColor="currentColor"
                bgColor="transparent"
              />
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Fade>
  );
};

export default RecordFromPhoneScreen;
